import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, AlertCircle, AlertTriangle, CheckCircle2, Download, FileDown,
  Search, ChevronLeft, ChevronRight, UserPlus, ChevronDown, ChevronUp,
} from "lucide-react";

import { api, API_BASE, apiErrorMessage, downloadAuthenticatedFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const COMMIT_CHUNK_SIZE = 150;
const PAGE_SIZE = 25;

const EXPECTED_COLUMNS = ["HTNO", "Student Name", "Branch Name", "CGPA", "School", "Fee Cleared"];

const STATUS_BADGE = {
  ATTENDING: <Badge className="bg-emerald-600">Attending</Badge>,
  NOT_ATTENDING: <Badge variant="secondary">Not Attending</Badge>,
  BLOCKED: <Badge variant="destructive">Blocked</Badge>,
};

export default function Students() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null); // masterlist id currently registering

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [commitProgress, setCommitProgress] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(id);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/masterlist", { params: { q: debouncedQ, page, page_size: PAGE_SIZE } });
      setRows(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [debouncedQ, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFee = async (row, cleared) => {
    try {
      await api.patch(`/masterlist/${row.id}`, { fee_cleared: cleared });
      toast.success(cleared ? "Fee marked cleared" : "Fee marked not cleared");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const registerStudent = async (row) => {
    setRegistering(row.id);
    try {
      await api.post(`/masterlist/${row.id}/register`);
      toast.success(`${row.student_name || row.htno} registered`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setRegistering(null);
    }
  };

  const onValidate = async () => {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    setBusy(true); setReport(null); setCommitted(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/masterlist/upload/validate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      toast.success(`Validated: ${r.data.valid_count} valid, ${r.data.invalid_count} invalid`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!report || report.valid_count === 0) return;
    setBusy(true);
    const rowsToCommit = report.valid_rows;
    const chunks = [];
    for (let i = 0; i < rowsToCommit.length; i += COMMIT_CHUNK_SIZE) chunks.push(rowsToCommit.slice(i, i + COMMIT_CHUNK_SIZE));

    let totalInserted = 0;
    setCommitProgress({ done: 0, total: chunks.length, inserted: 0 });
    try {
      for (let i = 0; i < chunks.length; i++) {
        const r = await api.post("/masterlist/upload/commit", { rows: chunks[i] });
        totalInserted += r.data.inserted;
        setCommitProgress({ done: i + 1, total: chunks.length, inserted: totalInserted });
      }
      toast.success(`Added/updated ${totalInserted} masterlist entr${totalInserted === 1 ? "y" : "ies"}`);
      setCommitted(true);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
      setCommitProgress(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadAuthenticatedFile("/masterlist/template", "masterlist_upload_template.xlsx");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const downloadErrorReport = () => {
    if (!report) return;
    const headers = ["row_number", "htno", "errors"];
    const lines = [headers.join(",")].concat(
      report.invalid_rows.map((r) => [r.row_number, r.htno, `"${(r.errors || []).join("; ")}"`].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "masterlist_validation_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="students-page">
      <PageHeader
        title="Students (Masterlist)"
        subtitle="The eligibility list for convocation — fee clearance drives who can be registered."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-masterlist-template-btn">
              <FileDown size={14} className="mr-2" /> Download Template
            </Button>
            <Button
              variant="outline"
              onClick={() => setUploadOpen((o) => !o)}
              data-testid="toggle-masterlist-upload-btn"
            >
              <UploadCloud size={14} className="mr-2" /> Upload Masterlist
              {uploadOpen ? <ChevronUp size={14} className="ml-2" /> : <ChevronDown size={14} className="ml-2" />}
            </Button>
          </div>
        }
      />

      {uploadOpen && (
        <div className="bg-white border border-border rounded-xl p-8 mb-6">
          <p className="kicker mb-3">Step 1 — Select file</p>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <label className="inline-flex items-center gap-3 border border-dashed border-slate-300 rounded-md px-4 py-3 cursor-pointer hover:border-primary transition-colors">
              <UploadCloud size={18} />
              <span className="text-sm">{file ? file.name : "Choose .xlsx file"}</span>
              <input
                data-testid="masterlist-upload-file-input"
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <Button
              data-testid="masterlist-upload-validate-btn"
              className="bg-primary hover:bg-primary/90"
              onClick={onValidate}
              disabled={!file || busy}
            >
              Validate Upload
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Expected columns: <code className="bg-slate-100 px-1.5 py-0.5 rounded break-all">{EXPECTED_COLUMNS.join(", ")}</code>
          </p>
          <p className="text-xs text-slate-500 mt-2">
            <b>HTNO</b> is required. Re-uploading an existing HTNO updates that entry (fee status, branch, etc.) rather than duplicating it.
          </p>

          {report && (
            <div className="mt-6 border-t pt-6" data-testid="masterlist-validation-report">
              <p className="kicker mb-3">Step 2 — Validation Report</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="border rounded-md p-4">
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-3xl font-serif" data-testid="ml-report-total">{report.total}</p>
                </div>
                <div className="border rounded-md p-4">
                  <p className="text-xs text-slate-500">Valid</p>
                  <p className="text-3xl font-serif text-emerald-600" data-testid="ml-report-valid">{report.valid_count}</p>
                </div>
                <div className="border rounded-md p-4">
                  <p className="text-xs text-slate-500">Invalid</p>
                  <p className="text-3xl font-serif text-primary" data-testid="ml-report-invalid">{report.invalid_count}</p>
                </div>
                <div className="border rounded-md p-4">
                  <p className="text-xs text-slate-500">Warnings</p>
                  <p className="text-3xl font-serif text-amber-500" data-testid="ml-report-warnings">{report.warning_count || 0}</p>
                </div>
              </div>

              {report.warning_count > 0 && (
                <div className="mb-6">
                  <p className="kicker flex items-center gap-2 mb-3 text-amber-600"><AlertTriangle size={14} /> Valid, but with warnings</p>
                  <div className="overflow-x-auto max-h-64 border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>HTNO</TableHead>
                          <TableHead>Warnings</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.valid_rows.filter((r) => r.warnings?.length).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.row_number}</TableCell>
                            <TableCell className="font-mono text-xs">{r.htno}</TableCell>
                            <TableCell>
                              {r.warnings.map((w, j) => (
                                <Badge key={j} variant="outline" className="mr-1 mb-1 border-amber-400 text-amber-700 bg-amber-50">{w}</Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {report.invalid_rows.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="kicker flex items-center gap-2"><AlertCircle size={14} /> Invalid Rows</p>
                    <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="ml-download-errors-btn">
                      <Download size={14} className="mr-1" /> Download Error Report
                    </Button>
                  </div>
                  <div className="overflow-x-auto max-h-80 border rounded-md mb-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Row</TableHead>
                          <TableHead>HTNO</TableHead>
                          <TableHead>Errors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.invalid_rows.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.row_number}</TableCell>
                            <TableCell className="font-mono text-xs">{r.htno}</TableCell>
                            <TableCell>
                              {(r.errors || []).map((e, j) => (
                                <Badge key={j} variant="destructive" className="mr-1 mb-1">{e}</Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <div className="flex flex-col items-end gap-3">
                <div className="flex items-center justify-end gap-3 w-full">
                  {committed ? (
                    <p className="flex items-center gap-2 text-emerald-600" data-testid="ml-commit-done">
                      <CheckCircle2 size={16} /> Masterlist committed successfully.
                    </p>
                  ) : (
                    <Button
                      data-testid="ml-commit-upload-btn"
                      className="bg-primary hover:bg-primary/90"
                      disabled={report.valid_count === 0 || busy}
                      onClick={onCommit}
                    >
                      {commitProgress ? "Committing…" : `Commit Upload (${report.valid_count})`}
                    </Button>
                  )}
                </div>
                {commitProgress && (
                  <div className="w-full">
                    <Progress value={(commitProgress.done / commitProgress.total) * 100} />
                    <p className="text-xs text-slate-500 mt-2 text-right">
                      {commitProgress.inserted} done · batch {commitProgress.done} of {commitProgress.total}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-border rounded-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-3">
          <div className="relative w-full md:w-96">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="students-search"
              className="pl-8" placeholder="Search by roll no. or name…" value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <p className="text-sm text-slate-500 shrink-0">
            {total} record{total === 1 ? "" : "s"} · Page {page} / {pages}
          </p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>HTNO</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead className="text-center">CGPA</TableHead>
                <TableHead>School</TableHead>
                <TableHead className="text-center">Fee Cleared</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} data-testid={`ml-row-${r.htno}`}>
                  <TableCell className="font-medium">{r.htno}</TableCell>
                  <TableCell>{r.student_name}</TableCell>
                  <TableCell>{r.branch_name}</TableCell>
                  <TableCell className="text-center">{r.cgpa ?? "—"}</TableCell>
                  <TableCell>{r.school}</TableCell>
                  <TableCell className="text-center">
                    <Switch
                      data-testid={`fee-toggle-${r.htno}`}
                      checked={!!r.fee_cleared}
                      onCheckedChange={(v) => toggleFee(r, v)}
                    />
                  </TableCell>
                  <TableCell>{STATUS_BADGE[r.status]}</TableCell>
                  <TableCell className="text-right">
                    {r.registered ? (
                      <Badge variant="outline">Registered</Badge>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-primary/90 disabled:opacity-40"
                        data-testid={`register-btn-${r.htno}`}
                        disabled={!r.fee_cleared || registering === r.id}
                        title={!r.fee_cleared ? "Fee not cleared — blocked from registering" : undefined}
                        onClick={() => registerStudent(r)}
                      >
                        <UserPlus size={14} className="mr-1" /> Register
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">
                  No masterlist records. Upload one above to get started.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="students-prev">
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="students-next">
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
